import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import MermaidDiagram from '../common/MermaidDiagram';
import useStageJob from '../common/useStageJob';
import AgentActivityGraph from '../common/AgentActivityGraph';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  buildStageContext,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

const DIAGRAM_TABS = [
  { key: 'systemDiagram',     label: 'System Architecture' },
  { key: 'componentDiagram',  label: 'Components' },
  { key: 'dataModel',         label: 'Data Model' },
  { key: 'deploymentDiagram', label: 'Deployment' },
];

/**
 * Design Stage
 *
 * Generates:
 *  - System architecture Mermaid diagram
 *  - Component relationship diagram
 *  - Data model ER diagram
 *  - Deployment topology diagram
 *  - Tech stack table
 *  - API endpoint catalogue
 */
function DesignStage() {
  const [project, setProject] = useState(getProject());
  const [design, setDesign]   = useState(null);
  const [activeTab, setActiveTab] = useState('systemDiagram');
  const [error, setError]     = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    const p = getProject();
    const stage = p.stages?.design;
    if (stage?.data) setDesign(stage.data);
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    const data = result || {};
    setDesign(data);
    setStageData('design', data);
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);
  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleGenerate = async () => {
    setError(null);
    const context = buildStageContext('design');
    const comment = project?.stages?.design?.comment || '';

    try {
      const res = await runPipelineStage('design', context, comment);
      if (res.status === 'accepted') {
        patchStage('design', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) setActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate design.');
    }
  };

  const requirements = project?.stages?.requirements?.data;
  const hasDesign    = Boolean(design?.systemDiagram || design?.componentDiagram);
  const displayError = error || pollError;

  // Only show diagram tabs that have content
  const availableTabs = DIAGRAM_TABS.filter((t) => design?.[t.key]);

  return (
    <PipelineShell
      stageId="design"
      description="Generate visual architecture diagrams, component relationships, data model and tech stack details."
      canApprove={hasDesign}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleGenerate}
          disabled={polling || !requirements}
          title={!requirements ? 'Complete the Requirements stage first' : undefined}
        >
          {polling ? '⏳ Generating…' : design ? '↩ Regenerate' : '🏛️ Generate Design'}
        </button>
      }
    >
      {/* Context from requirements */}
      {requirements?.title && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: '0.85rem', color: '#166534', border: '1px solid #bbf7d0' }}>
          <strong>Building on:</strong> {requirements.title}
          {requirements.overview && ` — ${requirements.overview}`}
        </div>
      )}

      {!requirements && (
        <div className="pipeline-empty">
          <div className="pipeline-empty-icon">🔒</div>
          <p>Complete and approve the <strong>Requirements</strong> stage first.</p>
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>Generating architecture diagrams…</p>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>This usually takes 20–45 seconds.</p>
        </div>
      )}

      {displayError && !polling && (
        <div className="pipeline-error">{displayError}</div>
      )}

      {hasDesign && !polling && (
        <>
          {/* ── Diagram Tabs ──────────────────────────────────────────── */}
          {availableTabs.length > 0 && (
            <div className="pipeline-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="pipeline-tabs" style={{ padding: '0 16px' }}>
                {availableTabs.map((t) => (
                  <button
                    key={t.key}
                    className={`pipeline-tab-btn${activeTab === t.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div style={{ padding: '0 16px 16px' }}>
                {availableTabs.map((t) =>
                  activeTab === t.key ? (
                    <MermaidDiagram key={t.key} chart={design[t.key]} title={t.label} />
                  ) : null,
                )}
              </div>
            </div>
          )}

          {/* ── Tech Stack ────────────────────────────────────────────── */}
          {Array.isArray(design.techStack) && design.techStack.length > 0 && (
            <div className="pipeline-card">
              <div className="pipeline-card-title">Tech Stack</div>
              <table className="pipeline-tech-table">
                <thead>
                  <tr><th>Layer</th><th>Technology</th><th>Version</th><th>Purpose</th></tr>
                </thead>
                <tbody>
                  {design.techStack.map((t, i) => (
                    <tr key={i}>
                      <td><span className="pipeline-tag gray">{t.layer || t.category}</span></td>
                      <td><strong>{t.tech || t.technology}</strong></td>
                      <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{t.version || '—'}</td>
                      <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{t.purpose || t.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── API Endpoints ─────────────────────────────────────────── */}
          {Array.isArray(design.apiEndpoints) && design.apiEndpoints.length > 0 && (
            <div className="pipeline-card">
              <div className="pipeline-card-title">API Endpoints</div>
              <table className="pipeline-tech-table">
                <thead>
                  <tr><th>Method</th><th>Path</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {design.apiEndpoints.map((ep, i) => (
                    <tr key={i}>
                      <td>
                        <span className={`pipeline-tag ${
                          ep.method === 'GET' ? 'green' :
                          ep.method === 'POST' ? '' :
                          ep.method === 'DELETE' ? 'red' : 'yellow'
                        }`}>{ep.method}</span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{ep.path}</td>
                      <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{ep.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Security & Scalability ────────────────────────────────── */}
          <div className="pipeline-grid-2">
            {Array.isArray(design.securityConsiderations) && design.securityConsiderations.length > 0 && (
              <div className="pipeline-card">
                <div className="pipeline-card-title">🔒 Security Considerations</div>
                <ul className="pipeline-list">
                  {design.securityConsiderations.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {design.scalabilityNotes && (
              <div className="pipeline-card">
                <div className="pipeline-card-title">📈 Scalability</div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>{design.scalabilityNotes}</p>
              </div>
            )}
          </div>
        </>
      )}
    </PipelineShell>
  );
}

export default DesignStage;
